import React, { useMemo, useState } from 'react'
import { useAccessStore } from '@sphere/auth/data-access'
import {
  useAnnouncements,
  useAnnouncementModuleCapabilities,
  useDeleteAnnouncement,
} from '@sphere/communication/data-access'
import { Button, SkeletonText } from '@carbon/react'
import { Add, Bullhorn } from '@carbon/icons-react'
import { useToast } from '@sphere/shared/util'
import { AnnouncementCard } from './AnnouncementCard'
import { AnnouncementCreateModal } from './AnnouncementCreateModal'
import styles from './AnnouncementView.module.scss'

export const AnnouncementView: React.FC = () => {
  const { toast } = useToast()
  const bootstrapLoaded = useAccessStore(state => state.bootstrapLoaded)
  const scope = useAccessStore(state => state.scope)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const contextEstablishmentId = scope?.establishmentIds?.[0]
  const contextClassId = scope?.classIds?.[0]
  const listParams = useMemo(
    () => ({
      limit: 20,
      contextEstablishmentId,
      contextClassId,
    }),
    [contextEstablishmentId, contextClassId]
  )

  const { data: announcementsData, isLoading } = useAnnouncements(
    listParams,
    { enabled: bootstrapLoaded }
  )
  const { data: moduleCapabilities } = useAnnouncementModuleCapabilities({
    enabled: bootstrapLoaded,
  })

  const deleteMutation = useDeleteAnnouncement()

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: 'Annonce supprimée' })
      },
    })
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Fil d'actualité</h2>
        {moduleCapabilities?.canCreate && (
          <Button renderIcon={Add} onClick={() => setIsCreateOpen(true)}>
            Nouvelle annonce
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3].map(i => (
            <div key={i} className={styles.skeletonRow}>
              <SkeletonText width="30%" heading />
              <SkeletonText width="100%" />
            </div>
          ))}
        </div>
      ) : announcementsData?.data.length === 0 ? (
        <div className={styles.emptyState}>
          <Bullhorn size={48} className={styles.emptyIcon} />
          <h3 className={styles.emptyTitle}>Aucune annonce</h3>
          <p className={styles.emptyDescription}>
            Vous êtes à jour sur toutes les actualités.
          </p>
        </div>
      ) : (
        <div className={styles.announcementList}>
          {announcementsData?.data.map(announcement => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              onDelete={handleDelete}
              isDeletePending={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      <AnnouncementCreateModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        scopeEstablishmentId={contextEstablishmentId ?? ''}
      />
    </div>
  )
}
